<template>
  <div id="app" class="container-fluid">
    <full_head />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading delivery record...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchRecords">Retry</button>
      </div>

      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-baby-carriage"></i> Delivery</h3>
          <button class="btn-edit-header" @click="openAddModal">
            <i class="fas fa-plus"></i> Record Delivery
          </button>
        </div>
        <div class="card-body">
          <div v-if="!records.length" class="no-data">
            No delivery has been recorded for this visit yet.
          </div>
          <div v-else class="delivery-list">
            <div class="delivery-card" v-for="record in records" :key="record.id">
              <div class="delivery-card-header">
                <div>
                  <strong>{{ record.mode_of_delivery }}</strong>
                  <span class="delivery-date">{{ formatDateTime(record.delivery_datetime) }}</span>
                </div>
                <div>
                  <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn-icon btn-icon-danger" title="Delete" @click="deleteRecord(record.id)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <div class="delivery-body">
                <div class="delivery-row">
                  <span v-if="record.number_of_babies"><strong>Babies:</strong> {{ record.number_of_babies }}</span>
                  <span v-if="record.perineum_status"><strong>Perineum:</strong> {{ record.perineum_status }}</span>
                  <span v-if="record.estimated_blood_loss_ml"><strong>EBL:</strong> {{ record.estimated_blood_loss_ml }} ml</span>
                  <span v-if="record.placenta_delivery_method"
                    ><strong>Placenta:</strong> {{ record.placenta_delivery_method }}
                    ({{ record.placenta_complete ? "Complete" : "Incomplete" }})</span
                  >
                </div>
                <p v-if="record.indication" class="delivery-text"><strong>Indication:</strong> {{ record.indication }}</p>
                <p v-if="record.complications" class="delivery-text"><strong>Complications:</strong> {{ record.complications }}</p>
                <p v-if="record.notes" class="delivery-text"><strong>Notes:</strong> {{ record.notes }}</p>
                <p class="delivery-meta">
                  <span v-if="record.conducted_by">Conducted by {{ record.conducted_by }}</span>
                  <span v-if="record.assisted_by"> · Assisted by {{ record.assisted_by }}</span>
                </p>

                <div v-if="record.newborns && record.newborns.length" class="linked-newborns">
                  <strong>Newborn(s):</strong>
                  {{ record.newborns.length }} recorded — see Newborn Information tab
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DeliveryModal
      v-if="showModal"
      :visitId="visit_id"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_head from "./full_head.vue";
import DeliveryModal from "./DeliveryModal.vue";
import { generalApiRequest } from "../../../../../api/apiIndex";

export default {
  name: "MaternityDelivery",
  components: { full_head, DeliveryModal },
  data() {
    return {
      loading: false,
      error: null,
      visit_id: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.fetchRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const response = await generalApiRequest(`deliveries/visit/${this.visit_id}`, "GET", {}, 1);
        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load delivery record";
        }
      } catch (err) {
        console.error("Error fetching delivery record:", err);
        this.error = "An error occurred while loading the delivery record. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },
    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },
    async handleSaved() {
      this.closeModal();
      await this.fetchRecords();
    },
    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this delivery record?")) return;
      try {
        const response = await generalApiRequest(`deliveries/${id}`, "DELETE", {}, 1);
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete delivery record");
        }
      } catch (error) {
        console.error("Error deleting delivery record:", error);
        alert("Failed to delete delivery record. Please try again.");
      }
    },
    formatDateTime(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container-fluid { padding: 12px; background-color: #f5f7fa; min-height: 100vh; }
.main-content { margin-top: 12px; }
.loading-container, .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: white; border-radius: 10px; }
.loading-container i { font-size: 3rem; color: #667eea; margin-bottom: 1rem; }
.error-container i { font-size: 3rem; color: #dc3545; margin-bottom: 1rem; }
.btn-retry { background: #667eea; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.card { background: white; border-radius: 10px; border: 1px solid #e9ecef; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-bottom: 2px solid #dee2e6; }
.card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #495057; display: flex; align-items: center; gap: 8px; }
.card-header h3 i { color: #667eea; }
.btn-edit-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; padding: 7px 16px; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.card-body { padding: 16px 20px; }
.no-data { text-align: center; padding: 20px; color: #6c757d; font-size: 13px; font-style: italic; background: #f8f9fa; border-radius: 6px; }
.delivery-list { display: flex; flex-direction: column; gap: 14px; }
.delivery-card { border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; }
.delivery-card-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #f8f9fa; border-bottom: 1px solid #e9ecef; }
.delivery-date { margin-left: 10px; font-size: 12px; color: #6c757d; }
.btn-icon { background: none; border: none; color: #667eea; cursor: pointer; padding: 4px 6px; }
.btn-icon-danger { color: #dc3545; }
.delivery-body { padding: 12px 14px; }
.delivery-row { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; color: #495057; margin-bottom: 8px; }
.delivery-text { font-size: 13px; color: #495057; margin: 4px 0; }
.delivery-meta { font-size: 11px; color: #6c757d; font-style: italic; margin-top: 8px; }
.linked-newborns { margin-top: 10px; font-size: 12px; background: #eef2ff; color: #4338ca; padding: 6px 10px; border-radius: 6px; }
</style>
